import { useState } from "react";
import { Link } from "react-router";
import { MessageSquarePlus, Trash2 } from "lucide-react";
import {
  Body,
  Caption,
  Card,
  CardTitle,
  EmptyState,
  Subtitle,
  VButton,
  VTextarea,
} from "@/components/ui";
import { useCurrentUser } from "@/domains/auth";
import { useAssets } from "@/domains/assets";
import {
  AssetStatusBadge,
  AssetTypeIcon,
  assetTypeLabel,
  stripAssetTypePrefix,
} from "@/domains/assets";
import {
  useAddCaseNote,
  useRemoveAssetFromCase,
} from "@/domains/cases/api/mutations";
import type { CaseFile } from "@/domains/cases/api/types";
import { AssetPickerDialog } from "@/domains/cases/components/assetPickerDialog";

type CaseAssetsTabProps = {
  caseFile: CaseFile;
};

export function CaseAssetsTab({ caseFile }: CaseAssetsTabProps): React.JSX.Element {
  const currentUser = useCurrentUser();
  const { data: allAssets } = useAssets();
  const removeAsset = useRemoveAssetFromCase();
  const addNote = useAddCaseNote();
  const [noteDraft, setNoteDraft] = useState<Record<string, string>>({});

  const linkedAssets = (allAssets ?? []).filter((a) => caseFile.assetIds.includes(a.id));

  const actor = {
    id: currentUser.id,
    name: `${currentUser.firstName} ${currentUser.lastName}`,
  };

  const handleAddNote = (assetId: string): void => {
    const content = noteDraft[assetId]?.trim();
    if (!content) return;
    addNote.mutate(
      {
        caseId: caseFile.id,
        note: {
          authorId: actor.id,
          authorName: actor.name,
          content,
          scope: "asset",
          assetId,
        },
      },
      {
        onSuccess: () => setNoteDraft((prev) => ({ ...prev, [assetId]: "" })),
      },
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Subtitle>
          {linkedAssets.length === 0
            ? "Nog geen assets gekoppeld aan dit dossier."
            : `${linkedAssets.length} asset(s) gekoppeld aan dit dossier.`}
        </Subtitle>
        <AssetPickerDialog caseFile={caseFile} />
      </div>

      {linkedAssets.length === 0 ? (
        <EmptyState
          title="Nog geen assets toegevoegd"
          description="Klik op 'Asset toevoegen' om beschikbare assets voor te stellen op basis van de periode van dit dossier."
        />
      ) : (
        <div className="space-y-3">
          {linkedAssets.map((asset) => {
            const assetNotes = caseFile.notes.filter(
              (n) => n.scope === "asset" && n.assetId === asset.id,
            );
            return (
              <Card key={asset.id} padding="lg" className="space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <div className="flex size-10 items-center justify-center rounded-md bg-primary-50 text-primary-700">
                      <AssetTypeIcon type={asset.type} className="size-5" />
                    </div>
                    <div>
                      <Link
                        to={`/assets/${asset.id}`}
                        className="block text-sm font-semibold text-foreground hover:underline"
                      >
                        {asset.number} — {assetTypeLabel(asset.type)}
                      </Link>
                      <Caption as="p" tone="muted">
                        {stripAssetTypePrefix(asset.name, asset.type)}
                      </Caption>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <AssetStatusBadge status={asset.status} />
                    <VButton
                      variant="ghost"
                      size="icon"
                      aria-label="Asset loskoppelen"
                      onClick={() =>
                        removeAsset.mutate({
                          caseId: caseFile.id,
                          assetId: asset.id,
                          actor,
                        })
                      }
                      icon={<Trash2 className="size-4 text-error-600" aria-hidden />}
                    />
                  </div>
                </div>

                {assetNotes.length > 0 ? (
                  <div className="space-y-2 border-t border-border pt-3">
                    <CardTitle className="text-sm">Asset-opmerkingen</CardTitle>
                    <ul className="space-y-2">
                      {assetNotes.map((n) => (
                        <li
                          key={n.id}
                          className="rounded-md bg-muted/40 px-3 py-2"
                        >
                          <div className="flex items-baseline justify-between gap-2">
                            <Body weight="semibold">{n.authorName}</Body>
                            <Caption tone="muted">
                              {n.createdAt.slice(0, 16).replace("T", " ")}
                            </Caption>
                          </div>
                          <Body>{n.content}</Body>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                <div className="flex gap-2 border-t border-border pt-3">
                  <VTextarea
                    rows={2}
                    placeholder="Opmerking specifiek voor deze asset…"
                    value={noteDraft[asset.id] ?? ""}
                    onChange={(e) =>
                      setNoteDraft((prev) => ({ ...prev, [asset.id]: e.target.value }))
                    }
                  />
                  <VButton
                    variant="outline"
                    onClick={() => handleAddNote(asset.id)}
                    disabled={
                      addNote.isPending || (noteDraft[asset.id] ?? "").trim().length === 0
                    }
                  >
                    <MessageSquarePlus className="size-4" aria-hidden />
                  </VButton>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
